import React from 'react';
import { Box, Skeleton, useTheme } from '@mui/material';
import { AnalysisStep } from '../context/AppContext';
import ProgressStepper from './ProgressStepper';

interface SkeletonLoaderProps {
  progress: AnalysisStep;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ progress }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: 800,
        width: '100%',
        mx: 'auto',
        gap: 3,
        backgroundColor: isDark ? 'rgba(27, 27, 48, 0.7)' : 'rgba(255, 255, 255, 0.6)',
        borderRadius: '24px',
        padding: 4,
      }}
    >
      {/* Playlist card skeleton */}
      <Skeleton
        variant="rounded"
        animation="wave"
        sx={{ width: 300, height: 80, borderRadius: '16px' }}
      />

      {/* Progress stepper */}
      <ProgressStepper currentStep={progress} />

      {/* Heatmap area skeleton — square with shimmer gradient */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          aspectRatio: '1 / 1',
          borderRadius: '24px',
          background: isDark
            ? 'linear-gradient(135deg, #1B1B30 0%, #252545 25%, #2a2a50 50%, #252545 75%, #1B1B30 100%)'
            : 'linear-gradient(135deg, #f0f0f0 0%, #e8e8f0 25%, #dde0f0 50%, #e8e8f0 75%, #f0f0f0 100%)',
          backgroundSize: '400% 400%',
          animation: 'shimmer 3s ease infinite',
          boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.08)',
          '@keyframes shimmer': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
        }}
      />

      {/* Summary text skeletons */}
      <Box sx={{ width: '100%', maxWidth: 500, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Skeleton variant="text" animation="wave" sx={{ width: '90%', height: 20 }} />
        <Skeleton variant="text" animation="wave" sx={{ width: '75%', height: 20 }} />
        <Skeleton variant="text" animation="wave" sx={{ width: '60%', height: 20 }} />
      </Box>
    </Box>
  );
};

export default SkeletonLoader;
